import { THEMES } from '@constants/constants'
import {
  useCreateIntegrationsByPosition,
  useEditPageUpdates,
  useIsEditPage,
  useViralLink,
  useVisitorId,
} from '@hooks'
import { usePlayerTrackingEvents } from '@hooks/lib/usePlayerTrackingEvents'
import useIsOwnerVideo from '@hooks/useIsOwnerVideo'
import { Campaign, DynamicVariables, EnvConfigShare } from '@interfaces'
import { NextPageWithLayout } from '@pages/_app'
import { removeQueryParamsFromRouter } from '@utils/index'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useEffect, useRef} from 'react'
import { VideoPlayerEvents } from '@sendspark/player/dist/VideoPlayer'
import { useAnalyticsTracking } from '@hooks/lib/useAnalyticsTracking'
import { useAnalyticsSetup } from '@hooks/lib/useAnalyticsSetup'
import { useCampaignUpdates } from '@hooks/lib/useCampaignUpdates'
import { useVideoPlayerState } from '@hooks/lib/useVideoPlayerState'
import LegacyTheme from '@components/Themes/Legacy'
import V2Theme from '@components/Themes/V2'
import { BaseThemeProps } from '@components/Themes/interfaces'
import APMTheme from '@components/Themes/APM'

interface ViewCampaignProps {
  campaign: Campaign
  ignoreTracking: boolean
  env: EnvConfigShare
  isLinkedInRequest: boolean
  dynamicVariables: DynamicVariables
  custom404?: boolean
  segmentApiKey?: string
  pusherKey?: string
  pusherCluster?: string
  isProtectedCampaign?: boolean
  setCampaign: Dispatch<SetStateAction<Campaign>>
}

const SharePage: NextPageWithLayout<ViewCampaignProps> = ({
  campaign,
  ignoreTracking,
  env,
  isLinkedInRequest,
  dynamicVariables,
  setCampaign,
}) => {
  const { checkIsOwnerVideo, isOwnerVideo } = useIsOwnerVideo({ campaign, env })
  const isEditPage = useIsEditPage()
  const visitorId = useVisitorId()
  const campaignUpdate = useEditPageUpdates(
    campaign.id,
    env,
    setCampaign,
    campaign.creator?._id,
    campaign.version
  )

  const {
    callToAction,
    showCta,
    title,
    description,
    pageTitle,
  } = useCampaignUpdates({
    campaign,
    env,
    setCampaign,
    isEditPage,
    campaignUpdate,
    isDynamicVariablesEnabled: (campaign?.featureFlips?.dynamicVariables && campaign.dynamicVariablesEnable) || false
  })

  const {
    prospectTrackVariables,
    analitycsIdentityValue,
  } = useAnalyticsSetup({ campaign, dynamicVariables })

  // Owned here, not in the theme, so the one CTA-tracking function can reach the player
  // whichever theme mounts it (SK-567). Themes bind it to the player they render.
  const playerEventsRef = useRef<VideoPlayerEvents | null>(null)

  const { trackCTAClick, trackViralLink, ahoyAnalyticsTracker, segmentTracker, analytics } = useAnalyticsTracking({
    campaign,
    env,
    isEditPage,
    isOwnerVideo,
    isLinkedInRequest,
    ignoreTracking,
    campaignUpdate,
    dynamicVariables,
    prospectTrackVariables,
    playerEventsRef,
  })

  const {
    setShowViralLoop,
    replayVideo,
    setReplayVideo,
    handleOnReplay,
    isViralModalOpen
  } = useVideoPlayerState({ campaign, isEditPage, analitycsIdentityValue })

  const playerEvents = usePlayerTrackingEvents(
    segmentTracker,
    campaign,
    visitorId,
    setShowViralLoop,
    prospectTrackVariables
  )

  const viralLink = useViralLink({
    campaignId: campaign?._id,
    referralCode: campaign?.referralCode || '',
    workspaceName: campaign?.workspaceName,
    currentSite: 'share page',
  })

  const router = useRouter()

  useEffect(() => {
    console.log('dynamicVariables', dynamicVariables)
    const dynamicVariableKeys = Object.keys(dynamicVariables)
    if (!dynamicVariableKeys.length) return
    removeQueryParamsFromRouter(router, dynamicVariableKeys)
  }, [])

  useEffect(() => {
    if (campaign?.userIntegrations?.length > 0) {
      useCreateIntegrationsByPosition(campaign.userIntegrations)
    }
  }, [campaign?.userIntegrations?.length])

  useEffect(() => {
    checkIsOwnerVideo()
  }, [analytics, campaign])

  const isV2Theme = campaign?.theme?.name === THEMES?.SENDSPARK
  const isAPMTheme = campaign?.theme?.name === THEMES?.APM
  const isMultipleCTAsSupported = campaign?.featureFlips?.supportMultipleCTAs || false;

  const baseProps: BaseThemeProps = {
    campaign,
    env,
    pageTitle,
    isLinkedInRequest,
    isOwnerVideo,
    ahoyAnalyticsTracker,
    legacyAnalytics: analytics,
    playerEvents,
    isEditPage,
    title,
    description,
    showCta,
    callToAction,
    isMultipleCTAsSupported,
    trackCTAClick,
    trackViralLink,
    viralLink,
    replayVideo,
    setReplayVideo,
    playerEventsRef,
  }

  if (isV2Theme) {
    return <V2Theme
      {...baseProps}
      dynamicVariables={dynamicVariables}
    />
  }

  if (isAPMTheme) {
    return <APMTheme
      {...baseProps}
      dynamicVariables={dynamicVariables}
    />
  }

  return <LegacyTheme
    {...baseProps}
    isViralModalOpen={isViralModalOpen}
    setShowViralLoop={setShowViralLoop}
    handleOnReplay={handleOnReplay}
  />
}

export default SharePage
